/**
 * Signup forms that go straight to a Klaviyo list (30 Sep): «Próximos
 * lanzamientos». The email-signup block renders `.mm-klaviyo-signup` inside the
 * form when its `mm_klaviyo_list` and `mm_klaviyo_key` settings are filled.
 *
 * Why not the Shopify customer form: every Shopify subscriber is synced by the
 * Klaviyo integration into one list («Newsletter (Main)»), with no way to split
 * it by tag or form, so launch signups would get the newsletter's welcome
 * discount and campaigns. Here the address is sent to Klaviyo's Client API
 * (the public, browser-facing one, keyed by the account's public key) for that
 * list only, and no Shopify customer is created. The integration's «To Shopify»
 * sync has to stay on «profiles that already exist in Shopify», or Klaviyo
 * would create the customer itself.
 *
 * The form keeps its look and its messages: the native success line, or an
 * error line with Klaviyo's reason when it gives one (an invalid address).
 * Measured before writing: the endpoint answers CORS for any origin and
 * validates the address (400, «Invalid email address»).
 */

const ENDPOINT = 'https://a.klaviyo.com/client/subscriptions';
const REVISION = '2025-10-15';

/** @param {SubmitEvent} event */
async function onSubmit(event) {
  const form = event.target;
  if (!(form instanceof HTMLFormElement)) return;

  const config = form.querySelector('.mm-klaviyo-signup');
  if (!(config instanceof HTMLElement)) return;

  event.preventDefault();

  const input = form.querySelector('input[type="email"]');
  const button = form.querySelector('button[type="submit"], button:not([type])');
  const success = form.querySelector('[data-mm-klaviyo-success]');
  const error = form.querySelector('[data-mm-klaviyo-error]');
  const errorText = form.querySelector('[data-mm-klaviyo-error-text]');
  const fallback = errorText?.getAttribute('data-default') ?? errorText?.textContent ?? '';
  if (errorText && !errorText.hasAttribute('data-default')) errorText.setAttribute('data-default', fallback);

  const email = input instanceof HTMLInputElement ? input.value.trim() : '';
  if (!email || form.hasAttribute('aria-busy')) return;

  form.setAttribute('aria-busy', 'true');
  if (button instanceof HTMLButtonElement) button.disabled = true;
  success?.setAttribute('hidden', '');
  error?.setAttribute('hidden', '');

  try {
    const response = await fetch(`${ENDPOINT}?company_id=${encodeURIComponent(config.dataset.key ?? '')}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/vnd.api+json', revision: REVISION },
      body: JSON.stringify({
        data: {
          type: 'subscription',
          attributes: {
            custom_source: config.dataset.source,
            profile: {
              data: {
                type: 'profile',
                attributes: {
                  email,
                  subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } },
                },
              },
            },
          },
          relationships: { list: { data: { type: 'list', id: config.dataset.list } } },
        },
      }),
    });

    if (response.ok) {
      if (input instanceof HTMLInputElement) input.value = '';
      success?.removeAttribute('hidden');
      if (success instanceof HTMLElement) success.focus();
    } else {
      const body = await response.json().catch(() => null);
      const invalidEmail = body?.errors?.some((item) => item?.source?.pointer?.endsWith('/email'));
      if (errorText) errorText.textContent = invalidEmail ? input?.validationMessage || fallback : fallback;
      error?.removeAttribute('hidden');
    }
  } catch {
    if (errorText) errorText.textContent = fallback;
    error?.removeAttribute('hidden');
  } finally {
    form.removeAttribute('aria-busy');
    if (button instanceof HTMLButtonElement) button.disabled = false;
  }
}

document.addEventListener('submit', onSubmit);
